import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sendSlackMessageTracked } from "../../../models/notifications";
import ModernPricingView from "../../pricing/modernPricingView";

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckIcon({ color = "#22c55e" }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

function XIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

function TrendIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
            <polyline points="17 6 23 6 23 12"/>
        </svg>
    );
}

function RetainIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
    );
}

function TagIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>
    );
}

function StarIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
    );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function Hero({ onCTA }) {
    return (
        <section style={{ background: "#050505", padding: "100px 24px 80px", textAlign: "center" }}>
            <div style={{ maxWidth: "720px", margin: "0 auto" }}>
                <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "18px" }}>
                    Cashback vs Discounts
                </p>
                <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: "22px" }}>
                    Discounts get you a sale. Cashback gets you a customer.
                </h1>
                <p style={{ fontSize: "17px", color: "#9ca3af", lineHeight: 1.65, maxWidth: "560px", margin: "0 auto 36px" }}>
                    Discount codes lower your price and train shoppers to wait for the next sale. Store credit and cashback reward the purchase at full price and pull customers back for the next order.
                </p>
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                        onClick={onCTA}
                        style={{ padding: "14px 28px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
                    >
                        Start free trial
                    </button>
                    <a
                        href="/pricing"
                        style={{ padding: "14px 28px", background: "transparent", color: "#e5e7eb", border: "1px solid #2a2a2a", borderRadius: "10px", fontSize: "14px", fontWeight: 600, cursor: "pointer", textDecoration: "none" }}
                    >
                        See pricing
                    </a>
                </div>
            </div>
        </section>
    );
}

function StatsStrip() {
    const stats = [
        { value: "2-3x", label: "higher repeat purchase rate with cashback vs discount programs" },
        { value: "72%", label: "of first-time buyers never place a second order from discount-led stores" },
        { value: "15%", label: "average increase in customer lifetime value with cashback" },
        { value: "5x", label: "cheaper to retain a customer than to acquire a new one" },
    ];
    return (
        <section style={{ background: "#111", borderTop: "1px solid #1a1a1a", borderBottom: "1px solid #1a1a1a", padding: "40px 24px" }}>
            <div style={{ maxWidth: "960px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "32px", textAlign: "center" }}>
                {stats.map((s) => (
                    <div key={s.label}>
                        <div style={{ fontSize: "28px", fontWeight: 800, color: "#22c55e", marginBottom: "6px" }}>{s.value}</div>
                        <div style={{ fontSize: "12.5px", color: "#6b7280", lineHeight: 1.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function DiscountProblemSection() {
    const problems = [
        {
            icon: <TagIcon />,
            title: "Discounts train customers to wait",
            body: "Once a customer sees a 20% off code, they expect it on every order. Over time, your full price becomes the price nobody actually pays, and your margins shrink with it.",
        },
        {
            icon: <RetainIcon />,
            title: "Discount buyers don't come back",
            body: "Coupon hunters move to wherever the next deal is. They weren't loyal to your brand, they were loyal to the price. When the discount disappears, so do they.",
        },
        {
            icon: <TrendIcon />,
            title: "Each discount compounds the problem",
            body: "The first discount attracts a price-sensitive cohort. Retaining that cohort requires more discounts. You end up needing higher customer acquisition spend just to break even.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        The Problem With Discounts
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Discounts solve short-term problems by creating long-term ones
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {problems.map((p) => (
                        <div key={p.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{p.icon}</div>
                            <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>{p.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65 }}>{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CashbackAdvantageSection() {
    const points = [
        {
            icon: <TrendIcon />,
            title: "Full-price purchases stay full price",
            body: "Cashback is issued after the purchase, not applied to it. The customer pays full price and receives credit for the next order. Your margin on the first sale is intact.",
        },
        {
            icon: <RetainIcon />,
            title: "The credit creates the next visit",
            body: "A customer with $12 in store credit has a built-in reason to come back. They didn't just buy something, they have something waiting for them at your store.",
        },
        {
            icon: <StarIcon />,
            title: "Perceived value goes up, not down",
            body: "Receiving credit after a purchase feels like a reward. Getting 20% off a price feels like that price was inflated. One builds confidence in the brand, the other undermines it.",
        },
        {
            icon: <TagIcon />,
            title: "Keeps high-margin customers loyal",
            body: "Customers who pay full price are your best customers. Cashback rewards them for doing so. Discounts do the opposite: they filter in price-sensitive shoppers and filter out margin-positive ones.",
        },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Why Cashback Works
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Cashback rewards purchasing behavior instead of discounting it
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {points.map((p) => (
                        <div key={p.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{p.icon}</div>
                            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>{p.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65, margin: 0 }}>{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ComparisonSection() {
    const rows = [
        { label: "Protects full-price margins", cashback: true, discount: false },
        { label: "Pulls customers back for a second order", cashback: true, discount: false },
        { label: "Works without a coupon code", cashback: true, discount: false },
        { label: "Increases perceived value", cashback: true, discount: false },
        { label: "Filters for loyal buyers", cashback: true, discount: false },
        { label: "Effective for one-time flash sales", cashback: false, discount: true },
        { label: "Easy to distribute externally", cashback: false, discount: true },
        { label: "Clears end-of-line inventory fast", cashback: false, discount: true },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "820px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Side by Side
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        When to use each one
                    </h2>
                </div>
                <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", borderBottom: "1px solid #1e1e1e", padding: "14px 24px" }}>
                        <div />
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#22c55e", textAlign: "center" }}>Cashback / store credit</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>Discount codes</div>
                    </div>
                    {rows.map((row, i) => (
                        <div
                            key={row.label}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 160px 160px",
                                padding: "14px 24px",
                                borderBottom: i < rows.length - 1 ? "1px solid #1a1a1a" : "none",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ fontSize: "13.5px", color: "#d1d5db" }}>{row.label}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.cashback ? <CheckIcon /> : <XIcon />}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.discount ? <CheckIcon color="#9ca3af" /> : <XIcon />}</div>
                        </div>
                    ))}
                </div>
                <p style={{ fontSize: "12.5px", color: "#4b5563", textAlign: "center", marginTop: "16px" }}>
                    Discounts still have a place for liquidation and one-time acquisition campaigns. For retention, cashback outperforms them consistently.
                </p>
            </div>
        </section>
    );
}

function LuxuryBrandsSection() {
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "780px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Pricing Integrity
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Why premium brands don't run discount codes
                    </h2>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                        <p style={{ fontSize: "14px", color: "#9ca3af", lineHeight: 1.7, margin: 0 }}>
                            Louis Vuitton, Rolex, and Patek Philippe do not issue discount codes. This is not an oversight. A discount signals that the original price was soft. For a brand built on perceived value, that signal is more damaging than the margin saved.
                        </p>
                    </div>
                    <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                        <p style={{ fontSize: "14px", color: "#9ca3af", lineHeight: 1.7, margin: 0 }}>
                            Cashback avoids this. The customer pays the full price, which confirms it. The store credit comes after, framed as a reward for the purchase. The product's value is never negotiated down.
                        </p>
                    </div>
                    <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                        <p style={{ fontSize: "14px", color: "#9ca3af", lineHeight: 1.7, margin: 0 }}>
                            You do not need to be a luxury brand to benefit from this framing. Any Shopify store with a product worth full price can use cashback to retain buyers without training them to expect a deal.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function OtherToolsSection() {
    const tools = [
        {
            name: "Gift cards",
            pro: "Good for acquiring new customers and gifting occasions. Recipients often spend more than the card value.",
            con: "One-time incentive. Does not build a repeat-purchase habit on its own.",
        },
        {
            name: "Promo codes",
            pro: "Fast to deploy and easy to track attribution from ad campaigns.",
            con: "Leak easily, get aggregated on coupon sites, and reduce perceived price permanently.",
        },
        {
            name: "Store credit",
            pro: "Flexible. Can be issued for returns, service recovery, or loyalty. Keeps spending inside your store.",
            con: "Requires a system to track and apply it at checkout. Pabloo handles this automatically.",
        },
        {
            name: "Cashback",
            pro: "Rewards full-price purchases, brings customers back, and compounds over time into a loyal buyer base.",
            con: "Requires upfront setup and a platform to manage credit balances.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Full Picture
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        All the tools, honestly compared
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                    {tools.map((t) => (
                        <div key={t.name} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px" }}>
                            <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#fff", marginBottom: "14px" }}>{t.name}</h3>
                            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                                <CheckIcon />
                                <p style={{ fontSize: "13px", color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>{t.pro}</p>
                            </div>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <XIcon />
                                <p style={{ fontSize: "13px", color: "#6b7280", lineHeight: 1.6, margin: 0 }}>{t.con}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA({ onCTA }) {
    return (
        <section style={{ background: "#0a0a0a", borderTop: "1px solid #1a1a1a", padding: "80px 24px", textAlign: "center" }}>
            <div style={{ maxWidth: "560px", margin: "0 auto" }}>
                <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: "16px" }}>
                    Replace your next discount with cashback
                </h2>
                <p style={{ fontSize: "15px", color: "#9ca3af", lineHeight: 1.6, marginBottom: "32px" }}>
                    14-day free trial. Works with your existing Shopify store. No coupon codes to manage.
                </p>
                <button
                    onClick={onCTA}
                    style={{ padding: "15px 32px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
                >
                    Start free trial
                </button>
            </div>
        </section>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

const CashbackVsDiscountsComponent = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        sendSlackMessageTracked("Lead is visiting the Cashback VS Discounts page.", "#website-updates");
    }, []);

    const handleCTA = () => { window.location.href = "https://app.pabloo.com/sign-up"; };

    return (
        <div style={{ background: "#050505" }}>
            <Hero onCTA={handleCTA} />
            <StatsStrip />
            <DiscountProblemSection />
            <CashbackAdvantageSection />
            <ComparisonSection />
            <LuxuryBrandsSection />
            <OtherToolsSection />
            <ModernPricingView />
            <FinalCTA onCTA={handleCTA} />
        </div>
    );
};

export default CashbackVsDiscountsComponent;
